import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BOOKS_META from '../data/booksMeta';
import KJV_BIBLE from '../data/kjvBible';
import { stripFormatting, formatVerse } from '../data/formatVerse';

const RESULTS_PER_PAGE = 25;

function getBookName(id) {
  const b = BOOKS_META.find(b => b.id === id);
  return b ? b.name : '';
}

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const doSearch = useCallback(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 2) return;

    const words = q.split(/\s+/);
    const found = [];

    for (let bid = 1; bid <= 66; bid++) {
      const bookData = KJV_BIBLE[String(bid)];
      if (!bookData) continue;
      for (const ch in bookData) {
        const verses = bookData[ch];
        verses.forEach((text, i) => {
          const plain = stripFormatting(text).toLowerCase();
          if (words.every(w => plain.includes(w))) {
            found.push({
              book: bid,
              chapter: parseInt(ch),
              verse: i + 1,
              text,
              ref: `${getBookName(bid)} ${ch}:${i + 1}`,
            });
          }
        });
      }
    }

    setResults(found);
    setPage(0);
    setSearched(true);
  }, [query]);

  const total = results.length;
  const totalPages = Math.ceil(total / RESULTS_PER_PAGE);
  const start = page * RESULTS_PER_PAGE;
  const end = Math.min(start + RESULTS_PER_PAGE, total);
  const pageResults = results.slice(start, end);

  const highlightText = (text) => {
    const formatted = formatVerse(text);
    // For search highlighting we need to work with the raw HTML approach
    // We'll use a simpler text-based approach for the search results
    return formatted;
  };

  return (
    <section>
      <div className="inner">
        <div className="section-label">KJV Bible Search</div>
        <h2 className="section-title">Search the Scriptures</h2>
        <p style={{ maxWidth: 600, fontSize: '.95rem', lineHeight: 1.8, color: 'rgba(245,240,232,.7)', marginBottom: '1.5rem', fontStyle: 'italic' }}>
          "Search the scriptures; for in them ye think ye have eternal life: and they are they which testify of me." John 5:39
        </p>

        <div className="search-container">
          <div className="search-box">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Enter a word, phrase, or topic..."
            />
            <button onClick={doSearch}>🔍</button>
          </div>

          {searched && (
            <div className="search-stats">
              {total > 0
                ? `${total.toLocaleString()} RESULTS FOUND · SHOWING ${start + 1}-${end}`
                : 'NO RESULTS FOUND'
              }
            </div>
          )}

          {searched && total === 0 && (
            <p style={{ color: 'rgba(245,240,232,.6)', marginTop: '1rem' }}>
              Try a different keyword or phrase. All 31,102 verses of the KJV are searchable.
            </p>
          )}

          {pageResults.map((r, idx) => (
            <div key={`${r.book}-${r.chapter}-${r.verse}-${idx}`} className="search-result">
              <div
                className="sr-ref"
                onClick={() => navigate(`/bible/${r.book}/${r.chapter}`)}
              >
                {r.ref}
              </div>
              <p className="sr-text">{highlightText(r.text)}</p>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="search-pagination">
              {page > 0 && (
                <button onClick={() => setPage(page - 1)}>◄ Prev</button>
              )}
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const p = Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
                if (p >= totalPages) return null;
                return (
                  <button
                    key={p}
                    className={p === page ? 'active' : ''}
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </button>
                );
              })}
              {page < totalPages - 1 && (
                <button onClick={() => setPage(page + 1)}>Next ►</button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
